/**
 * Shared pointer/mouse interaction helpers for 2D canvas engines.
 *
 * The big interaction orchestrators (DataTable's useTableGestures,
 * CanvasGraph's Interaction.ext.ts) stay engine-specific because they dispatch
 * to engine-shaped hit-test results. What's shared here is the small
 * independent machinery: coordinate normalization, event-type guards, and
 * click/drag/double-click detection against pointer-down + pointer-up timings.
 */

export type Point2D = { x: number; y: number };
export type ClientPointSource = { clientX: number; clientY: number };

/** Max distance (px) between pointer-down and pointer-up to still count as a click. */
export const CLICK_MOVEMENT_THRESHOLD = 5;

/** Max ms between pointer-down and pointer-up for the event to qualify as a click. */
export const CLICK_TIMEOUT_MS = 300;

/** Max ms between two consecutive clicks (at roughly the same spot) to trigger a double-click. */
export const DOUBLE_CLICK_TIMEOUT_MS = 300;

export function getElementLocalPoint(
  source: ClientPointSource,
  element: HTMLElement | null
): Point2D {
  if (!element) {
    return { x: source.clientX, y: source.clientY };
  }
  const rect = element.getBoundingClientRect();
  const scaleX = element.clientWidth > 0 ? rect.width / element.clientWidth : 1;
  const scaleY = element.clientHeight > 0 ? rect.height / element.clientHeight : 1;
  return {
    x: (source.clientX - rect.left) / scaleX,
    y: (source.clientY - rect.top) / scaleY,
  };
}

/**
 * Convert a mouse/pointer event's clientX/Y into canvas-local (logical) coordinates.
 *
 * Divides by the canvas's on-screen-vs-layout scale so hit-testing stays correct when
 * an ancestor applies a CSS `transform: scale()` — e.g. an EC hosted on the zoomable
 * Atlas whiteboard. `getBoundingClientRect()` reflects that scale while `clientWidth`
 * does not, so their ratio recovers it; the ratio is 1 (no-op) when unscaled.
 */
export function getMousePosition(
  event: MouseEvent | PointerEvent,
  canvas: HTMLCanvasElement
): Point2D {
  return getElementLocalPoint(event, canvas);
}

/**
 * On-screen-vs-layout scale of a canvas: the factor that converts a visual
 * (post-CSS-transform) pixel delta into the canvas's untransformed layout
 * pixels. It's `1` when the canvas is unscaled and `2` under an ancestor
 * `transform: scale(0.5)` (e.g. the Data Table canvas-zoom, or the Atlas
 * whiteboard). Engine geometry — column/row widths, hit bounds — is stored in
 * layout pixels, so any value derived from a raw `clientX/Y` delta must
 * multiply by this to stay in engine space. Mirrors the per-axis ratio
 * getElementLocalPoint divides by.
 */
export function getElementCssScale(element: Element): Point2D {
  const rect = element.getBoundingClientRect();
  return {
    x: element.clientWidth > 0 && rect.width > 0 ? element.clientWidth / rect.width : 1,
    y: element.clientHeight > 0 && rect.height > 0 ? element.clientHeight / rect.height : 1,
  };
}

export function getCanvasCssScale(canvas: HTMLCanvasElement): Point2D {
  return getElementCssScale(canvas);
}

/** Euclidean distance between two points. */
export function getDistance(p1: Point2D, p2: Point2D): number {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

/**
 * Cheaper drag-threshold check (no sqrt). Returns true when the squared
 * distance from `from` to `to` exceeds `threshold`². The default (5²=25px)
 * matches CLICK_MOVEMENT_THRESHOLD so "not a click" and "is a drag" agree.
 */
export function isDragThresholdExceeded(
  from: Point2D,
  to: Point2D,
  threshold: number = CLICK_MOVEMENT_THRESHOLD
): boolean {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return dx * dx + dy * dy > threshold * threshold;
}

// ============================================================================
// Pointer event type guards
// ============================================================================

export function isPointerEvent(event: Event): event is PointerEvent {
  return event instanceof PointerEvent;
}

export function isLeftClick(event: PointerEvent): boolean {
  return event.button === 0;
}

export function isRightClick(event: PointerEvent): boolean {
  return event.button === 2;
}

// ============================================================================
// Click / double-click state machine
// ============================================================================

export type ClickState = {
  pointerDownTime: number;
  pointerDownPosition: Point2D | null;
  lastClickTime: number;
  lastClickPosition: Point2D | null;
  isPointerDown: boolean;
};

export function createClickState(): ClickState {
  return {
    pointerDownTime: 0,
    pointerDownPosition: null,
    lastClickTime: 0,
    lastClickPosition: null,
    isPointerDown: false,
  };
}

export function recordPointerDown(state: ClickState, position: Point2D): void {
  state.isPointerDown = true;
  state.pointerDownTime = Date.now();
  state.pointerDownPosition = position;
}

export function resetPointerDownState(state: ClickState): void {
  state.isPointerDown = false;
  state.pointerDownPosition = null;
}

/**
 * Whether the current pointer-up qualifies as a click: pointer was down, it
 * hasn't been longer than CLICK_TIMEOUT_MS, and the cursor hasn't moved more
 * than CLICK_MOVEMENT_THRESHOLD px.
 */
export function isValidClick(state: ClickState, currentPosition: Point2D): boolean {
  if (!state.isPointerDown || !state.pointerDownPosition) return false;

  const timeSinceDown = Date.now() - state.pointerDownTime;
  const distance = getDistance(state.pointerDownPosition, currentPosition);

  return timeSinceDown <= CLICK_TIMEOUT_MS && distance <= CLICK_MOVEMENT_THRESHOLD;
}

/** Whether this click should be treated as the second half of a double-click. */
export function isDoubleClick(state: ClickState, currentPosition: Point2D): boolean {
  if (!state.lastClickPosition) return false;

  const timeSinceLastClick = Date.now() - state.lastClickTime;
  const distance = getDistance(state.lastClickPosition, currentPosition);

  return timeSinceLastClick <= DOUBLE_CLICK_TIMEOUT_MS && distance <= CLICK_MOVEMENT_THRESHOLD;
}

export function recordClick(state: ClickState, position: Point2D): void {
  state.lastClickTime = Date.now();
  state.lastClickPosition = position;
}

export function resetDoubleClickState(state: ClickState): void {
  state.lastClickTime = 0;
  state.lastClickPosition = null;
}
